// S4 frontend — TanStack Query hooks for the v0.15.0 backend.
// One file because the surface is large and the call sites are
// shallow; each section is annotated.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";

// ---------------------------------------------------------------------------
// Roles & permissions
// ---------------------------------------------------------------------------
export interface PermissionCatalogRow {
  key: string;
  description: string;
  category: string;
}

export interface Role {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  is_system_role: boolean;
  permissions: string[];
  user_count: number;
}

export function usePermissionCatalog() {
  return useQuery({
    queryKey: ["roles", "permissions"],
    queryFn: async () => {
      const { data } = await api.get<PermissionCatalogRow[]>(
        "/api/v1/roles/permissions",
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const { data } = await api.get<Role[]>("/api/v1/roles");
      return data;
    },
  });
}

export function useRole(id: string | undefined) {
  return useQuery({
    queryKey: ["roles", id],
    queryFn: async () => {
      const { data } = await api.get<Role>(`/api/v1/roles/${id}`);
      return data;
    },
    enabled: Boolean(id),
  });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      description?: string;
      permissions: string[];
    }) => {
      const { data } = await api.post<Role>("/api/v1/roles", input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["roles"] }),
  });
}

export function useUpdateRole(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name?: string;
      description?: string;
      permissions?: string[];
    }) => {
      const { data } = await api.put<Role>(`/api/v1/roles/${id}`, input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["roles"] }),
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/roles/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["roles"] }),
  });
}

// ---------------------------------------------------------------------------
// User invitations — accept (public). U1b-FE: useInviteUser moved to
// api/users.ts (the single user-management module) and gained legacy_role.
// ---------------------------------------------------------------------------
export function useAcceptInvite() {
  return useMutation({
    mutationFn: async (input: {
      token: string;
      password: string;
      full_name?: string;
    }) => {
      const { data } = await api.post<{
        access_token: string;
        refresh_token: string;
        expires_in: number;
        user_id: string;
      }>("/api/v1/users/accept-invite", input);
      return data;
    },
  });
}

// U1b-FE: the user role-assignment hooks (useUserRoles / useAssignUserRole /
// useRevokeUserRole) and useSuspendUser / useReactivateUser moved to
// api/users.ts (the single user-management module).

// ---------------------------------------------------------------------------
// MFA
// ---------------------------------------------------------------------------
export interface MFAEnrollResponse {
  totp_uri: string;
  qr_code_base64: string;
  backup_codes: string[];
}

export function useMFAEnroll() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<MFAEnrollResponse>(
        "/api/v1/auth/mfa/enroll",
        {},
      );
      return data;
    },
  });
}

export function useMFAVerifyEnrollment() {
  return useMutation({
    mutationFn: async (input: { totp_code: string }) => {
      const { data } = await api.post<{ message: string }>(
        "/api/v1/auth/mfa/verify-enrollment",
        input,
      );
      return data;
    },
  });
}

export function useMFADisable() {
  return useMutation({
    mutationFn: async (input: {
      totp_code: string;
      current_password: string;
    }) => {
      const { data } = await api.post<{ message: string }>(
        "/api/v1/auth/mfa/disable",
        input,
      );
      return data;
    },
  });
}

export function useMFARegenerateBackupCodes() {
  return useMutation({
    mutationFn: async (input: { totp_code: string }) => {
      const { data } = await api.post<{ backup_codes: string[] }>(
        "/api/v1/auth/mfa/regenerate-backup-codes",
        input,
      );
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------
export interface SessionRow {
  id: string;
  ip_address: string | null;
  user_agent: string | null;
  created_at: { utc: string; local: string; timezone: string } | null;
  last_active_at: { utc: string; local: string; timezone: string } | null;
  expires_at: { utc: string; local: string; timezone: string } | null;
  is_current: boolean;
}

export function useSessions() {
  return useQuery({
    queryKey: ["auth", "sessions"],
    queryFn: async () => {
      const { data } = await api.get<SessionRow[]>(
        "/api/v1/auth/sessions",
      );
      return data;
    },
  });
}

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      await api.delete(`/api/v1/auth/sessions/${sessionId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["auth", "sessions"] }),
  });
}

// ---------------------------------------------------------------------------
// API tokens
// ---------------------------------------------------------------------------
export interface APIToken {
  id: string;
  name: string;
  permissions: string[];
  created_at: { utc: string; local: string; timezone: string } | null;
  last_used_at: { utc: string; local: string; timezone: string } | null;
  expires_at: { utc: string; local: string; timezone: string } | null;
  revoked: boolean;
}

export function useAPITokens() {
  return useQuery({
    queryKey: ["api-tokens"],
    queryFn: async () => {
      const { data } = await api.get<APIToken[]>("/api/v1/api-tokens");
      return data;
    },
  });
}

export function useCreateAPIToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      permissions: string[];
      expires_at?: string | null;
    }) => {
      const { data } = await api.post<{
        id: string;
        name: string;
        token: string;
        permissions: string[];
        expires_at: string | null;
        warning: string;
      }>("/api/v1/api-tokens", input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-tokens"] }),
  });
}

export function useRevokeAPIToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/api-tokens/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["api-tokens"] }),
  });
}

// ---------------------------------------------------------------------------
// SSO
// ---------------------------------------------------------------------------
export interface SSOConfig {
  id: string;
  tenant_id: string;
  provider_name: string;
  idp_entity_id: string;
  idp_sso_url: string;
  sp_entity_id: string;
  attribute_mapping: Record<string, string>;
  default_role_id: string | null;
  allow_jit_provisioning: boolean;
  is_enabled: boolean;
}

export function useSSOConfig() {
  return useQuery({
    queryKey: ["sso", "config"],
    queryFn: async () => {
      const { data } = await api.get<SSOConfig | null>(
        "/api/v1/sso/config",
      );
      return data;
    },
  });
}

export function useUpsertSSOConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      provider_name: string;
      idp_metadata_xml: string;
      attribute_mapping?: Record<string, string>;
      default_role_id?: string | null;
      allow_jit_provisioning: boolean;
      is_enabled: boolean;
    }) => {
      const { data } = await api.post<SSOConfig>(
        "/api/v1/sso/config",
        input,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sso", "config"] }),
  });
}

export function useDeleteSSOConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.delete("/api/v1/sso/config");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sso", "config"] }),
  });
}

// ---------------------------------------------------------------------------
// Change password
// ---------------------------------------------------------------------------
export function useChangePassword() {
  return useMutation({
    mutationFn: async (input: {
      current_password: string;
      new_password: string;
      confirm_password: string;
    }) => {
      const { data } = await api.post("/api/v1/auth/change-password", input);
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// Password policy — client-side mirror of the backend rules so we
// can render a live strength indicator without round-tripping.
// ---------------------------------------------------------------------------
export interface PasswordRule {
  key: string;
  label: string;
  test: (s: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  { key: "len", label: "At least 10 characters", test: (s) => s.length >= 10 },
  { key: "upper", label: "One uppercase letter", test: (s) => /[A-Z]/.test(s) },
  { key: "lower", label: "One lowercase letter", test: (s) => /[a-z]/.test(s) },
  { key: "digit", label: "One number", test: (s) => /\d/.test(s) },
  {
    key: "special",
    label: "One special character",
    test: (s) => /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(s),
  },
];

export function passwordAllPass(s: string): boolean {
  return PASSWORD_RULES.every((r) => r.test(s));
}
